/* ==========================================================
   TWD Motion — scroll reveals
   Pairs with custom.css section "18 · Motion".
   No scroll listeners on the reveals, no library.
   Transform + opacity only. Each element animates once, then
   the classes are removed so theme hover/transitions are untouched.
 
   TWO TREATMENTS, on purpose:
     rise   — short text rows. Crisp, triggers as they enter.
     reveal — tall product cards. Slower, softer, with a touch of
              scale, and it waits until the row is properly on
              screen. A card triggered at the bottom edge finishes
              its animation before the shopper has scrolled it into
              view, which is why Similar Products looked static.
 
   SELECTOR POLICY
     Anchor on something the merchant controls — a collection
     handle, or the section TYPE in the id ("__product_recommendations")
     — never a generated block id like "collection_list_XPzfqL".
     Those change the moment a section is rebuilt and the animation
     silently stops. That is what happened to Shop by strap.
   ========================================================== */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.Shopify && window.Shopify.designMode) return; // keep the theme editor static
  if (!('IntersectionObserver' in window)) return;
 
  // Shopify wraps every section in #shopify-section-{section.id}
    /* Match on the section KEY only. A Shopify section id looks like
     shopify-section-template--27977106817333__section_gMkpbN. The number is the
     TEMPLATE id — tied to the theme, and it changes with every theme copy,
     including every version update. The part after __ comes from
     templates/index.json and does not change. Anchoring on the full id means
     these animations die silently on the next update, which is what happened
     on 30 Sep 2026. */
  const sec = (key) => '[id*="__' + key + '"]';
 
  // Shop by strap — found by its own card links, so a rebuild can't break it
  const STRAP = '.shopify-section:has(.group-block__link[href*="/collections/steel-straps"])';
 
  // Similar products — matched on the section TYPE, not the generated suffix
  const SIMILAR = '.shopify-section[id*="__product_recommendations"]';
 
  /* District Pick.
     It used to be a Featured product section; it is now an "Image with text"
     section holding a text group-block and an image-block. "Image with text"
     has no distinctive section type in the id (every generic section is
     __section_xxxxxx), and CSS/JS cannot match on the eyebrow text, so this
     one honestly needs the generated suffix.
 
     IF THE SECTION IS EVER DELETED AND RE-ADDED, the suffix changes and both
     the animation AND the mobile side-by-side layout stop working silently.
     The same id is hard-coded in custom.css §22 — update both together. */
    const PICK = sec('section_gMkpbN');
 
  /* ---- RISE: short text rows ---- */
  const RISE = [
    '.twd-trust__summary',                                               // trust band summary line
    '.twd-size__caption',                                                // Find Your Size caption
  ];
 
  /* ---- SETTLE: wrappers containing an <img>; the image settles to 1 ---- */
  const SETTLE = [
       sec('collection_list_MipGJ6') + ' .collection-card__image',          // Shop Men / Women             
    PICK + ' .image-block',                                              // District Pick watch
    '.twd-size__stage',                                                  // Find Your Size background
  ];
 
  /* ---- STAGGER: lists whose direct children rise one after another ---- */
  const STAGGER = [
    PICK + ' .section-content-wrapper > .group-block > .group-block-content', // District Pick: eyebrow, title, price, specs, button
    STRAP + ' .section-content-wrapper',                                 // Shop by strap: heading, 3 banners, link
    sec('section_DGKykX') + ' .section-content-wrapper',                 // About Us: text, then link                
    '.twd-trust__row',                                                   // Authentic / Warranty / Delivery
    '.twd-size__options',                                                // 30 / 36 / 40 / 44 mm buttons
  ];
 
  /* ---- REVEAL: the slower treatment, for tall cards ---- */
  const REVEAL = [];
  const REVEAL_STAGGER = [];
 
  /* ---- Engine ----
     One factory, two observers. They differ in when they fire and how
     far apart the steps are; the look is set by the class in CSS. */
  const makeReveal = ({ rootMargin, step, maxSteps, clearAfter }) => {
    const io = new IntersectionObserver((entries) => {
      let n = 0; // position within this batch, so each row staggers from zero
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        io.unobserve(el);
 
        let delay = 0;
        if (el.dataset.twdStagger) {
          delay = Math.min(n++, maxSteps) * step;
          el.style.setProperty('--twd-delay', delay + 'ms');
        }
        el.classList.add('is-in');
 
        setTimeout(() => {
          el.classList.remove('twd-rise', 'twd-settle', 'twd-reveal', 'is-in');
          el.style.removeProperty('--twd-delay');
        }, clearAfter + delay + 100);
      }
    }, { rootMargin, threshold: 0 });
 
    // "Already seen" = its top is above the line this observer triggers on.
    const cut = 1 - (parseFloat(rootMargin.split(' ')[2]) || 0) / -100;
    const inView = (el) => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight * cut && r.bottom > 0;
    };
 
    const arm = (el, cls, stagger) => {
      if (el.dataset.twd) return;
      el.dataset.twd = '1';
      if (inView(el)) return; // never hide what is already on screen (hero, LCP)
      if (stagger) el.dataset.twdStagger = '1';
      el.classList.add(cls);
      io.observe(el);
    };
 
    /* Watch the LIST, animate its children.
       Not the same as arming each child. These lists become horizontal
       carousels at narrow widths, and a card sitting off to the right
       never intersects the viewport — so it would stay at opacity 0
       for good and the row would look half empty. Observing the list
       means every card animates once the row itself is on screen,
       which is right for a grid and for a carousel. */
    const armGroup = (list, cls, late) => {
      const rows = [...list.children].filter(
        (c) =>
          c.tagName !== 'STYLE' &&
          c.tagName !== 'SCRIPT' &&
          // Loading placeholders are not content; never animate them.
          !c.classList.contains('product-recommendations__skeleton-item')
      );
      if (!rows.length) return;
 
      /* Flag the ROWS, not the list. <product-recommendations> keeps the
         same .resource-list and swaps its children, so a flag on the list
         would block re-arming when the real cards replace the skeletons. */
      if (rows.every((r) => r.dataset.twd)) return;
      rows.forEach((r) => { r.dataset.twd = '1'; });
 
      /* `late` = these rows just arrived from a fetch, replacing a skeleton.
         Skip the on-screen check in that case. The check exists to avoid
         hiding content the shopper is already reading — but a skeleton is
         not content, so animating over it is right, and it's the only way
         to get the SAME result every time. Without this, whether it
         animates depends on how fast you happened to scroll: the fetch
         fires as the section nears the viewport, so a slow scroll means
         the cards land already on screen and it silently does nothing. */
      if (!late && inView(list)) return;
 
      rows.forEach((r) => r.classList.add(cls));
 
      const once = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        once.disconnect();
        rows.forEach((row, i) => {
          const delay = Math.min(i, maxSteps) * step;
          row.style.setProperty('--twd-delay', delay + 'ms');
          row.classList.add('is-in');
          setTimeout(() => {
            row.classList.remove('twd-rise', 'twd-settle', 'twd-reveal', 'is-in');
            row.style.removeProperty('--twd-delay');
          }, clearAfter + delay + 100);
        });
      }, { rootMargin, threshold: 0 });
 
      once.observe(list);
    };
 
    return { arm, armGroup };
  };
 
  // Short rows: fire as they enter, tight steps.
  const rise = makeReveal({ rootMargin: '0px 0px -12% 0px', step: 140, maxSteps: 4, clearAfter: 1200 });
  const settle = makeReveal({ rootMargin: '0px 0px -12% 0px', step: 140, maxSteps: 4, clearAfter: 1600 });
 
  // Tall cards: wait until the row is a quarter of the way up the screen,
  // then move slowly with wider spacing between cards.
  const reveal = makeReveal({ rootMargin: '0px 0px -26% 0px', step: 120, maxSteps: 4, clearAfter: 1800 });
 
  /* The trust strip runs on the product page too, directly above Similar
     Products. Two animated bands back to back read as one confused block,
     so on product pages the trust strip stays still and the motion belongs
     to Similar Products alone. It still animates on the homepage. */
  const onProductPage = /\/products\//.test(location.pathname);
 
  // A selector the browser can't parse is skipped, not fatal
  const each = (sel, fn) => {
    try { document.querySelectorAll(sel).forEach(fn); } catch (e) { /* skip */ }
  };
 
  // Theme sections drop a <style> in among the blocks; it isn't a row.
  const rowsOf = (list) =>
    [...list.children].filter((c) => c.tagName !== 'STYLE' && c.tagName !== 'SCRIPT');
 
  const skip = (s) => onProductPage && s.startsWith('.twd-trust');  // see onProductPage above
 
  RISE.forEach((s) => { if (!skip(s)) each(s, (el) => rise.arm(el, 'twd-rise')); });
  SETTLE.forEach((s) => each(s, (el) => settle.arm(el, 'twd-settle')));
  STAGGER.forEach((s) => {
    if (skip(s)) return;
    each(s, (l) => rowsOf(l).forEach((c) => rise.arm(c, 'twd-rise', true)));
  });
 
  const armReveals = (late) => {
    REVEAL.forEach((s) => each(s, (el) => reveal.arm(el, 'twd-reveal')));
    REVEAL_STAGGER.forEach((s) => each(s, (l) => reveal.armGroup(l, 'twd-reveal', late)));
  };
  armReveals(false);
 
  /* <product-recommendations> ships loading SKELETONS and fetches the real
     cards later. This script runs long before that, so on its own it arms
     .product-recommendations__skeleton-item placeholders — which are then
     thrown away and replaced by unarmed cards. That is the whole reason
     Similar Products never animated, on desktop or on mobile.
     Re-arm whenever the section's contents change. Rows already handled
     carry data-twd, so this is cheap and can't double-animate. */
  each(SIMILAR, (section) => {
    if (section.dataset.twdWatch) return;
    section.dataset.twdWatch = '1';
    new MutationObserver(() => armReveals(true)).observe(section, {
      childList: true,
      subtree: true,
    });
  });
 
  /* ---- Hero: headline fades and lifts, video drifts slower behind it ----
     At most one update per frame, and only while the hero is on screen.
     Transform + opacity only. */
  const hero = document.querySelector('.hero__container');
  if (hero) {
    /* The page does NOT scroll the window on desktop. html and body are
       overflow:hidden at viewport height, and the real scrolling element is
       .page-wrapper. So window.scrollY never moves and window never hears a
       scroll event — which is why this ran on phones (where the window does
       scroll) and did nothing on desktop. Find the actual scroller rather
       than assuming which it is, so both cases work. */
    const scroller = (() => {
      let el = hero.parentElement;
      while (el && el !== document.documentElement) {
        const oy = getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 4) return el;
        el = el.parentElement;
      }
      return null; // window scrolls
    })();
 
    const source = scroller || window;
    const getY = () => (scroller ? scroller.scrollTop : window.scrollY || 0);
 
    // only a hero at the top of the page
    if (hero.getBoundingClientRect().top + getY() < 200) {
      const text = hero.querySelector('.hero__content-wrapper');
      const media = hero.querySelector('.hero__media-wrapper');
      hero.classList.add('twd-hero');
 
      let h = hero.offsetHeight;
      let visible = true;
      let ticking = false;
 
      const update = () => {
        ticking = false;
        const y = Math.min(getY(), h);
        const p = Math.min(y / (h * 0.55), 1);        // headline fully gone at 55% of hero height
        if (text) {
          text.style.opacity = String(1 - p);
          text.style.transform = 'translate3d(0,' + (-p * 48).toFixed(1) + 'px,0)';
          text.style.pointerEvents = p > 0.9 ? 'none' : '';
        }
        if (media) {
          // scale 1.08 gives the drift room so no gap shows at the top edge
          media.style.transform = 'translate3d(0,' + (y * 0.2).toFixed(1) + 'px,0) scale(1.08)';
        }
      };
      const request = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
 
      new IntersectionObserver(([e]) => { visible = e.isIntersecting; request(); }).observe(hero);
      source.addEventListener('scroll', () => { if (visible) request(); }, { passive: true });
      window.addEventListener('resize', () => { h = hero.offsetHeight; request(); }, { passive: true });
      update();
    }
  }
})();
 
 
/* ==========================================================
   TWD product cards — always start on the first image
   The card's image strip is a scroller; browsers restore its
   position on reload, so a card can come back on image 2 or 3.
   scroll-behavior has to be forced to auto or the snap undoes it.
   ========================================================== */
(() => {
  const reset = () => {
    document.querySelectorAll('.card-gallery slideshow-slides').forEach((s) => {
      if (!s.scrollLeft) return;
      const prev = s.style.scrollBehavior;
      s.style.scrollBehavior = 'auto';
      s.scrollLeft = 0;
      s.scrollTo({ left: 0, behavior: 'auto' });
      s.style.scrollBehavior = prev;
    });
  };
  ['DOMContentLoaded', 'load', 'pageshow'].forEach((e) => addEventListener(e, reset));
  document.addEventListener('shopify:section:load', reset);
  [0, 300, 900, 1800].forEach((t) => setTimeout(reset, t));
})();
 
 
 
 
/* ==========================================================
   TWD District Pick — scroll-driven spin
   Pairs with sections/twd-pick-rotate.liquid and custom.css §23.
 
   The frame is a pure function of scroll position. Nothing here
   animates on a timer, nothing eases toward a target, nothing
   snaps — scroll up and it turns back, stop and it stops. That
   is the brief, and it is also why there is no smoothing: any
   lerp would make the watch lag behind the finger, which is the
   exact thing that reads as cheap.
 
   ONE CANVAS, NOT THIRTEEN IMAGES
     The first build stacked the frames and crossfaded opacity.
     It measured 60fps under a scripted scroll but stuttered in
     real use: `will-change: opacity` on thirteen 1040x1500 images
     promotes each to its own compositor layer, roughly 65MB of
     GPU texture pinned for the life of the page. Drawing into one
     canvas is one layer regardless of frame count, which is how
     Rolex's own sequence is built.
 
   HOLD, THEN BLEND
     Fading linearly across each step meant you spent 96% of the
     scroll looking at a 50/50 mix of two angles. Thirty degrees
     apart that is not motion blur, it is a double exposure — two
     bracelets, two bezels, the logo printed twice. So each frame
     holds crisp and alone for most of its step and the change
     happens quickly at the end of it.
 
     That is a mitigation, not a cure. A sequence looks liquid when
     neighbouring frames are a few degrees apart; with 13 frames at
     30 degrees there is a real jump however it is blended. More
     frames is the only actual fix.
 
   THE PROGRESS SUM
     The section is tall; a sticky box inside it holds the visual.
     Progress is how far the section has travelled past the top of
     the scroller, over the distance it CAN travel (its height
     minus the sticky box). Measured from getBoundingClientRect so
     it works whether the window scrolls or .page-wrapper does.
   ========================================================== */
(() => {
  const sections = document.querySelectorAll('[data-twd-spin]');
  if (!sections.length) return;
 
  /* Fallback share of each step spent changing frame, when the section
     does not set one. 0 = a hard cut between angles; 1 = a continuous
     crossfade. The section exposes this as "Transition softness". */
  const DEFAULT_BLEND = 0.45;
 
  /* The shape of the transition matters as much as its length.
     A linear or smoothstep fade lingers around 50/50, and a 50/50 mix
     of two angles 30 degrees apart is a double exposure — you see two
     bracelets. This curve is the opposite of smoothstep: gentle at
     both ends so neither frame pops in or out, and steep through the
     middle so the ghosted state passes quickly. */
  const K = 2.3;
  const TK = Math.tanh(K);
  const ease = (t) =>
    t <= 0 ? 0 : t >= 1 ? 1 : 0.5 * (1 + Math.tanh(K * (2 * t - 1)) / TK);
 
  const now = () => performance.now();
 
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
 
  /* Enables the tall scrolling layout in CSS. Without this class the
     section renders short and static, so if this file ever fails to
     load the page is not left with three screens of dead space. */
  const setMode = () => {
    document.documentElement.classList.toggle('twd-spin-js', !still.matches);
  };
  setMode();
  still.addEventListener?.('change', setMode);
  if (still.matches) return;
 
  /* Same problem as the hero: on desktop the window does not scroll,
     .page-wrapper does. Find the real scroller rather than assuming. */
  const scrollerOf = (el) => {
    let n = el.parentElement;
    while (n && n !== document.documentElement) {
      const oy = getComputedStyle(n).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 4) return n;
      n = n.parentElement;
    }
    return null;
  };
 
  sections.forEach((section) => {
    const sticky = section.querySelector('.twd-spin__sticky');
    const canvas = section.querySelector('.twd-spin__canvas');
    const fallback = section.querySelector('.twd-spin__fallback');
    const data = section.querySelector('[data-twd-spin-frames]');
    if (!sticky || !canvas || !data) return;
 
    let urls;
    try { urls = JSON.parse(data.textContent); } catch (e) { return; }
    if (!Array.isArray(urls) || urls.length < 2) return;
 
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;
 
    const raw = parseFloat(section.dataset.twdSpinBlend);
    const BLEND = Number.isFinite(raw) ? Math.max(0, Math.min(1, raw / 100)) : DEFAULT_BLEND;
 
    /* Motion smoothing, the one idea worth taking from GSAP's scrub.
       0 pins the frame to scroll position exactly. Above 0 the drawn
       position chases the scrolled position instead of matching it, so
       a flick that skips five frames ANIMATES through them rather than
       cutting to the end. The cost is that the watch trails your finger
       slightly; with few frames that trade is usually worth it. */
    const rawS = parseFloat(section.dataset.twdSpinSmooth);
    const SMOOTH = Number.isFinite(rawS) ? Math.max(0, Math.min(0.95, rawS / 100)) : 0.35;
 
    const scroller = scrollerOf(section);
    const source = scroller || window;
    const last = urls.length - 1;
    const imgs = new Array(urls.length);
 
    let visible = false;
    let ticking = false;
    let started = false;
    let drawn = false;
    let lastAt = now();
 
    /* Decoding thirteen images costs more than downloading them, and
       doing it during the first scroll is exactly when it is felt. So
       load and decode the whole sequence a screen before the section
       arrives, in order, and draw as soon as the first one lands. */
    const load = () => {
      if (started) return;
      started = true;
      urls.forEach((url, i) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => { imgs[i] = img; if (i === 0) request(); };
        img.src = url;
      });
    };
 
    /* The backing store is sized in device pixels, capped at 2x. Beyond
       2x the extra pixels are invisible and the fill rate is not. */
    const resize = () => {
      const r = canvas.getBoundingClientRect();
      if (!r.width || !r.height) return false;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.round(r.width * dpr);
      const h = Math.round(r.height * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      return true;
    };
 
    /* Where the scroll says we are, and where we are actually drawing.
       They are the same value when SMOOTH is 0. */
    let shown = null;
 
    const targetP = () => {
      const r = section.getBoundingClientRect();
      const origin = scroller ? scroller.getBoundingClientRect().top : 0;
      const travel = r.height - sticky.offsetHeight;
      if (travel <= 0) return null;
      return Math.max(0, Math.min(1, (origin - r.top) / travel));
    };
 
    const paint = () => {
      ticking = false;
      if (!resize()) return;
 
      const want = targetP();
      if (want === null) return;
 
      if (shown === null || SMOOTH <= 0) {
        shown = want;
      } else {
        /* Frame-rate independent easing: the same fraction of the gap is
           closed per unit of time whether the display runs at 60Hz or 120. */
        const k = 1 - Math.pow(1 - SMOOTH, Math.min(now() - lastAt, 50) / 16.7);
        shown += (want - shown) * k;
        if (Math.abs(want - shown) < 0.0004) shown = want;
      }
      lastAt = now();
 
      const p = shown;
      const exact = p * last;
      const base = Math.min(Math.floor(exact), last);
 
      let mix = exact - base;
      mix = BLEND <= 0 || mix <= 1 - BLEND
        ? 0
        : ease((mix - (1 - BLEND)) / BLEND);
 
      /* Never draw a gap. If the frame we want has not decoded yet,
         fall back to the nearest one that has, so the watch holds an
         angle instead of blinking out. */
      let a = imgs[base];
      if (!a) {
        for (let i = base; i >= 0 && !a; i--) a = imgs[i];
        for (let i = base + 1; i <= last && !a; i++) a = imgs[i];
      }
      if (!a) return;
 
      const b = mix > 0.004 ? imgs[base + 1] : null;
 
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1;
      ctx.drawImage(a, 0, 0, canvas.width, canvas.height);
      if (b) {
        ctx.globalAlpha = mix;
        ctx.drawImage(b, 0, 0, canvas.width, canvas.height);
        ctx.globalAlpha = 1;
      }
 
      /* The static first frame stays until the canvas has something
         real on it, so there is never a blank box. */
      if (!drawn) {
        drawn = true;
        if (fallback) fallback.hidden = true;
      }
 
      /* Still catching up: keep the loop alive until it settles, then
         stop entirely. No rAF runs while nothing is moving. */
      if (shown !== want && visible) request();
    };
 
    const request = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(paint);
    };
 
    new IntersectionObserver(
      ([e]) => {
        visible = e.isIntersecting;
        if (visible) { load(); request(); }
      },
      { rootMargin: '100% 0px' }      // a screen early
    ).observe(section);
 
    source.addEventListener('scroll', () => { if (visible) request(); }, { passive: true });
    window.addEventListener('resize', request, { passive: true });
  });
})();



/* ==========================================================
   TWD Similar Products — reveal

   Its own block since 30 Sep 2026. <product-recommendations>
   ships loading skeletons and fetches the real cards after, and
   the generic armGroup path stopped surviving that swap in Dwell
   4.2.0 — the heading animated alone above eight static cards.

   This waits instead. A card is only real once it contains a
   link; skeletons never do. Nothing is armed until the fetch has
   landed, so the heading and the cards animate as one sequence.

   Timings are the originals from the old `reveal` observer —
   120ms between cards, capped at 4 steps, triggering once the row
   is a quarter of the way up the screen. Uses .twd-reveal from
   custom.css §18. No new CSS.
   ========================================================== */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.Shopify && window.Shopify.designMode) return;
  if (!('IntersectionObserver' in window)) return;

  const STEP = 120;   // ms between cards
  const MAX  = 4;     // cap the stagger so a long row doesn't trail
  const HOLD = 1800;  // clear the classes after this + delay

  const run = (section) => {
    if (section.dataset.twdRev) return;

    const list = section.querySelector('.resource-list');
    if (!list) return;

    // A real card has a link; a skeleton does not. No cards yet = still loading.
    const cards = [...list.children].filter(
      (c) => c.classList.contains('resource-list__item') && c.querySelector('a[href]')
    );
    if (!cards.length) return;

    section.dataset.twdRev = '1';

    // Never hide what the shopper is already reading.
    const r = list.getBoundingClientRect();
    if (r.top < window.innerHeight * 0.85 && r.bottom > 0) return;

    const heading = section.querySelector('.section-resource-list__content');
    const targets = heading ? [heading, ...cards] : cards;
    targets.forEach((el) => el.classList.add('twd-reveal'));

    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      targets.forEach((el, i) => {
        const delay = Math.min(i, MAX) * STEP;
        el.style.setProperty('--twd-delay', delay + 'ms');
        el.classList.add('is-in');
        setTimeout(() => {
          el.classList.remove('twd-reveal', 'is-in');
          el.style.removeProperty('--twd-delay');
        }, HOLD + delay + 100);
      });
    }, { rootMargin: '0px 0px -26% 0px', threshold: 0 });

    io.observe(list);
  };

  document.querySelectorAll('[id*="__product_recommendations"]').forEach((section) => {
    run(section);
    new MutationObserver(() => run(section))
      .observe(section, { childList: true, subtree: true });
  });
})();